import Loading from 'components/Loading/Loading'
import NewUOMContextProvider, {
    useNewUOMContext,
} from './context/NewUOMContext'
import useGetUOMAndDependencies from './hooks/useGetUOMAndDependencies'
import { useUOMsContext } from 'pages/UOMsPage/context/UOMsContext'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ROUTE from 'constant/route'
import { UOMContract } from 'models'
import NewUOMForm from './NewUOMForm/NewUOMForm'
import AdminMenuTitle from 'components/AdminMenuTitle/AdminMenuTitle'
import { Grid } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { setBreadCrumbMapping } from 'store/actions/breadCrumb.action'
import errorMessages from 'utilities/errorMessages'
import { Dictionary } from 'utilities/typeUtility/typeUtility'
import { clone } from 'lodash-es'

function NewUOMLoad({
    uomID,
    onSubmit,
    submitting,
}: {
    uomID?: number
    submitting: boolean
    onSubmit: (contract: UOMContract) => Promise<void>
}) {
    const { loading, ...props } = useGetUOMAndDependencies(uomID)

    if (loading) return <Loading />

    return (
        <NewUOMContextProvider
            {...props}
            onSubmit={onSubmit}
            submitting={submitting}
        >
            <NewUOMSuccess />
        </NewUOMContextProvider>
    )
}

function NewUOMSuccess() {
    const { t } = useTranslation()
    const {
        submitting,
        onSubmit,
        uom,
        duplicatedUOMNames,
        duplcatedUOMSymbols,
        uomClasseOptions,
        uoms,
    } = useNewUOMContext()
    const dispatch = useDispatch()
    const history = useHistory()
    const [duplicatedNameList, setDuplicaterNameList] = useState<string[]>(
        duplicatedUOMNames,
    )

    const [duplcatedUOMSymbolList, setDuplcatedUOMSymbolList] = useState<
        Dictionary<string[]>
    >(duplcatedUOMSymbols)

    const onBack = useCallback(() => history.push(ROUTE.UOM_UOMS), [history])

    const onSave = useCallback(
        async (newUOM: UOMContract) => {
            await onSubmit(newUOM)
                .then(onBack)
                .catch((err) => {
                    const { message } = errorMessages(err)
                    if (message === 'uoms.message.error.name_duplicated')
                        setDuplicaterNameList((prev) => [...prev, newUOM.name!])
                    if (message === 'uoms.message.error.symbol_duplicated') {
                        var cloneDuplicatedSymbolList = clone(
                            duplcatedUOMSymbolList,
                        )
                        var updatedSymbols =
                            cloneDuplicatedSymbolList[newUOM.uomClassID!]
                        if (updatedSymbols) {
                            updatedSymbols.push(newUOM.symbol!)
                        } else {
                            cloneDuplicatedSymbolList[newUOM.uomClassID!] = [
                                newUOM.symbol!,
                            ]
                        }
                        setDuplcatedUOMSymbolList(cloneDuplicatedSymbolList)
                    }
                    throw err
                })
        },
        [onSubmit, onBack, duplcatedUOMSymbolList],
    )

    useEffect(() => {
        if (uom.uomid) {
            dispatch(
                setBreadCrumbMapping({
                    key: uom.uomid.toString(),
                    value: uom.name!,
                }),
            )
        }
    }, [dispatch, uom])

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <AdminMenuTitle
                    title={uom.uomid ? uom.name : t('uoms.title.new_uom')}
                />
            </Grid>
            <Grid item xs={12}>
                <NewUOMForm
                    uom={uom}
                    onCancel={onBack}
                    onSave={onSave}
                    submitting={submitting}
                    duplcatedUOMNames={duplicatedNameList}
                    duplcatedUOMSymbols={duplcatedUOMSymbolList}
                    uomClasses={uomClasseOptions}
                    uoms={uoms}
                />
            </Grid>
        </Grid>
    )
}

function NewUOM({ uomID }: { uomID?: number }) {
    const { loading, onAddUOM, onUpdateUOM, processing } = useUOMsContext()

    const onSubmit = useCallback(
        async (newUOM: UOMContract) => {
            if (uomID) await onUpdateUOM(uomID, newUOM)
            else await onAddUOM(newUOM)
        },
        [uomID, onUpdateUOM, onAddUOM],
    )

    if (loading) return <Loading />
    return (
        <NewUOMLoad uomID={uomID} onSubmit={onSubmit} submitting={processing} />
    )
}

export default NewUOM
